import { usStates } from "./community-settings/us-states";
import { validEmail } from "./email";
import { BadArgumentException } from "./error";
export const businessProfileStatuses = ["draft", "submitted"];
export const businessTypes = [
    "Limited Liability Corporation",
    "Corporation",
    "Partnership",
    "Non-profit Corporation",
    "Co-operative",
    "Sole Proprietorship",
];
export const supportedBusinessTypes = businessTypes.filter(type => type !== "Sole Proprietorship");
export const businessTypeLabels = {
    "Limited Liability Corporation": "LLC",
    Corporation: "Corporation",
    Partnership: "Partnership",
    "Non-profit Corporation": "Nonprofit",
    "Co-operative": "Co-operative",
    "Sole Proprietorship": "Sole proprietorship",
};
export const businessIndustries = [
    "HEALTHCARE",
    "AGRICULTURE",
    "AUTOMOTIVE",
    "BANKING",
    "CONSUMER",
    "EDUCATION",
    "ELECTRONICS",
    "ENERGY",
    "ENGINEERING",
    "FAST_MOVING_CONSUMER_GOODS",
    "FINANCIAL",
    "FINTECH",
    "FOOD_AND_BEVERAGE",
    "GOVERNMENT",
    "HOSPITALITY",
    "INSURANCE",
    "JEWELRY",
    "LEGAL",
    "MANUFACTURING",
    "MEDIA",
    "NOT_FOR_PROFIT",
    "OIL_AND_GAS",
    "ONLINE",
    "RAW_MATERIALS",
    "REAL_ESTATE",
    "RELIGION",
    "RETAIL",
    "TECHNOLOGY",
    "TELECOMMUNICATIONS",
    "TRANSPORTATION",
    "TRAVEL",
];
export const businessIndustryLabels = {
    HEALTHCARE: "Healthcare",
    AGRICULTURE: "Agriculture",
    AUTOMOTIVE: "Automotive",
    BANKING: "Banking",
    CONSUMER: "Consumer",
    EDUCATION: "Education",
    ELECTRONICS: "Electronics",
    ENERGY: "Energy",
    ENGINEERING: "Engineering",
    FAST_MOVING_CONSUMER_GOODS: "Fast moving consumer goods",
    FINANCIAL: "Financial",
    FINTECH: "Fintech",
    FOOD_AND_BEVERAGE: "Food and beverage",
    GOVERNMENT: "Government",
    HOSPITALITY: "Hospitality",
    INSURANCE: "Insurance",
    JEWELRY: "Jewelry",
    LEGAL: "Legal",
    MANUFACTURING: "Manufacturing",
    MEDIA: "Media",
    NOT_FOR_PROFIT: "Not for profit",
    OIL_AND_GAS: "Oil and gas",
    ONLINE: "Online",
    RAW_MATERIALS: "Raw materials",
    REAL_ESTATE: "Real estate",
    RELIGION: "Religion",
    RETAIL: "Retail",
    TECHNOLOGY: "Technology",
    TELECOMMUNICATIONS: "Telecommunications",
    TRANSPORTATION: "Transportation",
    TRAVEL: "Travel",
};
export const representativePositions = [
    "CEO",
    "CFO",
    "VP",
    "GM",
    "Director",
    "General Counsel",
    "Other",
];
export const representativePositionLabels = {
    CEO: "Owner or CEO",
    CFO: "CFO",
    VP: "Vice President",
    GM: "General Manager",
    Director: "Director",
    "General Counsel": "General Counsel",
    Other: "Other",
};
export const optInMethods = ["call", "webform", "paper", "inbound"];
export const privacyPolicyStatements = ["noShare", "frequency", "rates"];
export const requiredSampleMessageCount = 3;
export const minimumSampleMessageLength = 20;
export const maximumCallerIdNameLength = 15;
export const emptyBusinessAddress = () => ({
    street: "",
    suite: "",
    city: "",
    state: "",
    zip: "",
});
export const emptyBusinessRepresentative = () => ({
    firstName: "",
    lastName: "",
    title: "",
    position: null,
    email: "",
    phone: "",
});
export const isBusinessProfileField = (groups, field) => Object.prototype.hasOwnProperty.call(groups, field);
export const businessProfileFieldGroup = (groups, field) => {
    const topLevelField = field.split(".")[0];
    if (!isBusinessProfileField(groups, topLevelField)) {
        throw new BadArgumentException(`Unknown business profile field: ${field}`);
    }
    return groups[topLevelField];
};
export const emptyBusinessProfileDetails = () => ({
    legalName: "",
    doingBusinessAs: "",
    businessType: null,
    industry: null,
    ein: "",
    website: "",
    socialPage: "",
    address: emptyBusinessAddress(),
    representative: emptyBusinessRepresentative(),
    secondRepresentative: null,
    optInMethods: [],
    sampleMessages: [],
    privacyPolicyUrl: "",
    privacyPolicyStatements: [],
    callerIdName: "",
});
export const defaultSampleMessages = (businessName) => [
    `Hi [First name], this is [Advisor name] with ${businessName}. I have a few communities in mind for your mom. Is this afternoon a good time to talk? Reply STOP to opt out.`,
    `Hi [First name], this is [Advisor name] with ${businessName} confirming your tour at [Community name] on [Date] at [Time]. Call or text me at [Phone number] if anything changes. Reply STOP to opt out.`,
    `Hi [First name], it's [Advisor name] from ${businessName}. How did the tour feel to you? Happy to talk through next steps whenever you're ready. Reply STOP to opt out.`,
];
const digits = (value) => value.replace(/\D/g, "");
const isFilled = (value) => value.trim().length > 0;
const isPoBox = (value) => /\b(p\.?\s*o\.?|post\s+office)\s*box\b/i.test(value);
const isUsStateCode = (value) => usStates.some(state => state.code.toUpperCase() === value);
const problemFor = (key, problem) => problem ? { [key]: problem } : {};
const isLink = (value) => {
    const trimmed = value.trim();
    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
        ? trimmed
        : `https://${trimmed}`;
    try {
        return new URL(withScheme).hostname.length > 0;
    }
    catch {
        return false;
    }
};
const privacyPolicyUrlProblem = (privacyPolicyUrl) => isFilled(privacyPolicyUrl) && !isLink(privacyPolicyUrl)
    ? "Enter a link to your privacy policy."
    : null;
export const callerIdNameProblem = (value) => {
    if (!value) {
        return "Enter a caller ID name.";
    }
    if (value.length > maximumCallerIdNameLength) {
        return `Keep it to ${maximumCallerIdNameLength} characters or fewer.`;
    }
    if (!/^[A-Za-z]/.test(value)) {
        return "Start with a letter.";
    }
    if (!/^[A-Za-z0-9., ]+$/.test(value)) {
        return "Use only letters, numbers, periods, commas, and spaces.";
    }
    return null;
};
const einProblem = "Enter your 9 digit EIN.";
const poBoxProblem = "Use a street address, not a PO box.";
const zipProblem = "Enter a 5 digit ZIP code.";
const emailProblem = "Enter a valid email.";
const phoneProblem = "Enter a 10 digit phone number.";
const addressFormatProblems = (address) => ({
    ...problemFor("address.street", isPoBox(address.street) ? poBoxProblem : null),
    ...problemFor("address.suite", isPoBox(address.suite) ? poBoxProblem : null),
    ...problemFor("address.state", isFilled(address.state) && !isUsStateCode(address.state.trim())
        ? "Choose a US state."
        : null),
    ...problemFor("address.zip", isFilled(address.zip) && !/^\d{5}$/.test(address.zip.trim()) ? zipProblem : null),
});
const representativeFormatProblems = (key, representative) => ({
    ...problemFor(`${key}.email`, isFilled(representative.email) && !validEmail(representative.email)
        ? emailProblem
        : null),
    ...problemFor(`${key}.phone`, isFilled(representative.phone) && digits(representative.phone).length !== 10
        ? phoneProblem
        : null),
});
const callerIdNameFormatProblems = (callerIdName) => isFilled(callerIdName)
    ? problemFor("callerIdName", callerIdNameProblem(callerIdName))
    : {};
export const businessProfileFormatProblems = (details) => ({
    ...problemFor("ein", isFilled(details.ein) && digits(details.ein).length !== 9 ? einProblem : null),
    ...addressFormatProblems(details.address),
    ...representativeFormatProblems("representative", details.representative),
    ...(details.secondRepresentative
        ? representativeFormatProblems("secondRepresentative", details.secondRepresentative)
        : {}),
    ...problemFor("privacyPolicyUrl", privacyPolicyUrlProblem(details.privacyPolicyUrl)),
    ...callerIdNameFormatProblems(details.callerIdName),
});
const businessTypeProblem = (businessType) => {
    if (!businessType) {
        return "Choose a business type.";
    }
    return businessType === "Sole Proprietorship"
        ? "We can't verify sole proprietorships yet."
        : null;
};
const missingBusinessProblems = (details) => ({
    ...problemFor("legalName", !isFilled(details.legalName) ? "Enter your legal business name." : null),
    ...problemFor("doingBusinessAs", !isFilled(details.doingBusinessAs)
        ? "Enter the name customers know you by."
        : null),
    ...problemFor("businessType", businessTypeProblem(details.businessType)),
    ...problemFor("industry", !details.industry ? "Choose an industry." : null),
    ...problemFor("ein", !isFilled(details.ein) ? einProblem : null),
    ...problemFor("website", !isFilled(details.website) ? "Enter your website." : null),
});
const missingAddressProblems = (address) => ({
    ...problemFor("address.street", !isFilled(address.street) ? "Enter a street address." : null),
    ...problemFor("address.city", !isFilled(address.city) ? "Enter a city." : null),
    ...problemFor("address.state", !isFilled(address.state) ? "Enter a state." : null),
    ...problemFor("address.zip", !isFilled(address.zip) ? zipProblem : null),
});
const missingRepresentativeProblems = (key, representative) => ({
    ...problemFor(`${key}.firstName`, !isFilled(representative.firstName) ? "Enter a first name." : null),
    ...problemFor(`${key}.lastName`, !isFilled(representative.lastName) ? "Enter a last name." : null),
    ...problemFor(`${key}.title`, !isFilled(representative.title) ? "Enter a job title." : null),
    ...problemFor(`${key}.position`, !representative.position ? "Choose a position." : null),
    ...problemFor(`${key}.email`, !isFilled(representative.email) ? emailProblem : null),
    ...problemFor(`${key}.phone`, !isFilled(representative.phone) ? phoneProblem : null),
});
export const sampleMessageProblem = (message) => message.trim().length < minimumSampleMessageLength
    ? `Each sample message needs at least ${minimumSampleMessageLength} characters.`
    : null;
const sampleMessagesProblem = (sampleMessages) => {
    if (sampleMessages.length !== requiredSampleMessageCount) {
        return "Add three sample messages.";
    }
    return sampleMessages.map(sampleMessageProblem).find(Boolean) ?? null;
};
const missingTextingProblems = (details) => ({
    ...problemFor("optInMethods", details.optInMethods.length === 0
        ? "Tell us how people give you their number."
        : null),
    ...problemFor("sampleMessages", sampleMessagesProblem(details.sampleMessages)),
    ...problemFor("privacyPolicyUrl", !isFilled(details.privacyPolicyUrl) ? "Enter your privacy policy link." : null),
    ...problemFor("privacyPolicyStatements", !privacyPolicyStatements.every(s => details.privacyPolicyStatements.includes(s))
        ? "Check off the three privacy policy items."
        : null),
});
export const businessProfileSubmissionProblems = (details) => ({
    ...missingBusinessProblems(details),
    ...missingAddressProblems(details.address),
    ...missingRepresentativeProblems("representative", details.representative),
    ...(details.secondRepresentative
        ? missingRepresentativeProblems("secondRepresentative", details.secondRepresentative)
        : {}),
    ...missingTextingProblems(details),
    ...businessProfileFormatProblems(details),
});
//# sourceMappingURL=business-profile.js.map